/**
 * SubEnvCard.jsx — iter847 (Feb 2026)
 * Small band-coloured tile for one metabolic sub-environment (insulin /
 * IGF-1 / inflammation / estrogen / adiposity). Shows base+fusion delta
 * badge when DUTCH/DNA modifiers adjusted the score.
 *
 * iter893 — optional `missingLabsHint` surfaces "⚠️ missing labs" nudge
 * when a diagnosed condition (e.g. celiac, hashimoto) has no supporting
 * lab (hs-CRP / omega-3) to actually move the sub-score.
 */
import React from "react";
import { AlertTriangle } from "lucide-react";
import EvidenceBadge from "@/components/breast_intel/EvidenceBadge";

const BAND_PILL = {
  green:  "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  yellow: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  orange: "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300",
  red:    "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300",
  insufficient_data: "bg-slate-100 text-slate-500 dark:bg-slate-500/20 dark:text-slate-300",
};

export default function SubEnvCard({ name, score, band, confidence, evidenceId, lang, speclabDelta, baseScore, missingLabsHint, livingBoost, livingOnlyHint }) {
  const labels = {
    bg: { insulin: "Инсулин", igf1: "IGF-1", inflammation: "Възпаление", estrogen: "Естроген", adiposity: "Мастност" },
    en: { insulin: "Insulin", igf1: "IGF-1", inflammation: "Inflammation", estrogen: "Estrogen", adiposity: "Adiposity" },
    es: { insulin: "Insulina", igf1: "IGF-1", inflammation: "Inflamación", estrogen: "Estrógeno", adiposity: "Adiposidad" },
  };
  const hasFusion = typeof speclabDelta === "number" && Math.abs(speclabDelta) >= 0.5;
  const hasLiving = typeof livingBoost === "number" && Math.abs(livingBoost) >= 0.5;
  return (
    <div className={`p-3 rounded-xl text-center relative ${BAND_PILL[band]}`} data-testid={`sub-env-${name}`}>
      {hasFusion && (
        <span
          className={`absolute top-1 right-1 text-[9px] font-bold px-1 py-0.5 rounded-full hidden md:inline-flex ${
            speclabDelta < 0
              ? "bg-orange-200 text-orange-800 dark:bg-orange-500/30 dark:text-orange-200"
              : "bg-emerald-200 text-emerald-800 dark:bg-emerald-500/30 dark:text-emerald-200"
          }`}
          title={lang === "bg" ? `Базов скор: ${baseScore} · коригиран чрез DUTCH/DNA с ${speclabDelta > 0 ? "+" : ""}${speclabDelta}` : `Base: ${baseScore} · DUTCH/DNA shift ${speclabDelta > 0 ? "+" : ""}${speclabDelta}`}
          data-testid={`sub-env-${name}-delta`}
        >
          {speclabDelta > 0 ? "+" : ""}{speclabDelta}
        </span>
      )}
      {/* iter931 — Living-Metabolism 14-day adherence badge (kept visible
          on mobile — patient-facing motivational element) */}
      {hasLiving && (
        <span
          className={`absolute top-1 left-1 text-[9px] font-bold px-1 py-0.5 rounded-full ${
            livingBoost < 0
              ? "bg-rose-200 text-rose-800 dark:bg-rose-500/30 dark:text-rose-200"
              : "bg-fuchsia-200 text-fuchsia-800 dark:bg-fuchsia-500/30 dark:text-fuchsia-200"
          }`}
          title={lang === "bg" ? `14-дн. Живият метаболизъм: ${livingBoost > 0 ? "+" : ""}${livingBoost}` : `14-day Living Metabolism: ${livingBoost > 0 ? "+" : ""}${livingBoost}`}
          data-testid={`sub-env-${name}-living`}
        >
          🌱{livingBoost > 0 ? "+" : ""}{livingBoost}
        </span>
      )}
      <div className="text-[10px] opacity-80 mb-0.5">{labels[lang]?.[name] || name}</div>
      <div className="text-xl font-bold">{score == null ? "—" : score}</div>
      {score == null && typeof livingOnlyHint === "number" && Math.abs(livingOnlyHint) >= 0.5 && (
        <div className="text-[9px] mt-0.5 opacity-80"
             data-testid={`sub-env-${name}-living-only-hint`}>
          🌱 {lang === "bg" ? "Стил на живот" : "Lifestyle"} {livingOnlyHint > 0 ? "+" : ""}{livingOnlyHint}
        </div>
      )}
      {confidence && (
        <div className="mt-1 flex justify-center hidden md:flex">
          <EvidenceBadge confidence={confidence} evidenceId={evidenceId} lang={lang} showLabel={false} />
        </div>
      )}
      {missingLabsHint && (
        <div
          className="mt-1.5 items-start gap-1 text-[9px] leading-tight opacity-90 text-left hidden md:flex"
          data-testid={`sub-env-${name}-missing-labs`}
          title={missingLabsHint}
        >
          <AlertTriangle className="w-2.5 h-2.5 shrink-0 mt-0.5" />
          <span>{missingLabsHint}</span>
        </div>
      )}
    </div>
  );
}
